import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UserCircle, SignOut, Crown, List, X } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { NotificationBell } from './NotificationBell';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from './ui/sheet';
import { usernameColorClass } from '../lib/usernameColors';

export function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    setMobileOpen(false);
    await logout();
    navigate('/');
  };

  const closeMenu = () => setMobileOpen(false);

  const authedLinks = user ? (
    <>
      <Link to="/members" onClick={closeMenu} data-testid="members-link">
        <Button variant={location.pathname === '/members' ? 'default' : 'ghost'} className="rounded-full w-full md:w-auto justify-start md:justify-center">
          Members Area
        </Button>
      </Link>
      <Link to="/chat-rooms" onClick={closeMenu} data-testid="chatrooms-link">
        <Button variant={location.pathname === '/chat-rooms' ? 'default' : 'ghost'} className="rounded-full w-full md:w-auto justify-start md:justify-center">
          Chat Rooms
        </Button>
      </Link>
      <Link to="/support" onClick={closeMenu} data-testid="support-link">
        <Button variant={location.pathname === '/support' ? 'default' : 'ghost'} className="rounded-full w-full md:w-auto justify-start md:justify-center">
          Support
        </Button>
      </Link>
      {user.membership_level === 0 && (
        <Link to="/membership" onClick={closeMenu} data-testid="membership-link">
          <Button className="rounded-full bg-primary hover:bg-primary/90 w-full md:w-auto justify-start md:justify-center">
            <Crown size={16} className="mr-1" weight="fill" />
            Go Elite
          </Button>
        </Link>
      )}
      <Link to="/profile" onClick={closeMenu} className="flex items-center space-x-2 text-sm hover:text-primary transition-colors px-4 py-2 md:p-0" data-testid="profile-link">
        <UserCircle size={24} weight="duotone" />
        <span className={usernameColorClass(user.username_color)} data-testid="user-username">{user.username}</span>
      </Link>
    </>
  ) : (
    <>
      <Link to="/login" onClick={closeMenu} data-testid="login-link">
        <Button variant="ghost" className="rounded-full w-full md:w-auto">Login</Button>
      </Link>
      <Link to="/register" onClick={closeMenu} data-testid="register-link">
        <Button className="rounded-full bg-primary hover:bg-primary/90 w-full md:w-auto">Sign Up</Button>
      </Link>
    </>
  );

  return (
    <nav className="sticky top-0 z-50 backdrop-blur-2xl bg-black/60 border-b border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.5)]" data-testid="main-navbar">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 sm:gap-3" data-testid="logo-link">
            <img
              src="/logo.jpeg"
              alt="Truth Confession logo"
              className="h-10 w-10 sm:h-11 sm:w-11 rounded-md object-contain bg-white ring-1 ring-amber-400/40"
              data-testid="navbar-logo-image"
            />
            <h1 className="text-xl sm:text-2xl font-light tracking-tighter text-white">Truth<span className="bg-gradient-to-r from-amber-200 via-amber-400 to-amber-600 bg-clip-text text-transparent font-normal">Confession</span></h1>
          </Link>

          {/* Desktop links (hidden on mobile) */}
          <div className="hidden md:flex items-center space-x-4 lg:space-x-6">
            {authedLinks}
          </div>

          {/* Right side: always-on notification bell + desktop logout + mobile hamburger */}
          <div className="flex items-center space-x-1 sm:space-x-2">
            {user && <NotificationBell />}
            {user && (
              <Button
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="rounded-full hidden md:inline-flex"
                data-testid="logout-button"
              >
                <SignOut size={20} />
              </Button>
            )}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-full md:hidden"
                  data-testid="mobile-menu-button"
                  aria-label="Open menu"
                >
                  {mobileOpen ? <X size={24} /> : <List size={24} />}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-black/95 border-white/10 backdrop-blur-2xl w-[85vw] max-w-sm p-6" data-testid="mobile-menu">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <SheetDescription className="sr-only">Main navigation and account actions</SheetDescription>
                <div className="flex flex-col space-y-3 mt-8">
                  {authedLinks}
                  {user && (
                    <Button
                      onClick={handleLogout}
                      variant="outline"
                      className="rounded-full w-full mt-4 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                      data-testid="mobile-logout-button"
                    >
                      <SignOut size={20} className="mr-2" />
                      Sign Out
                    </Button>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
